import request from '../../services/Request';
import { currencies } from '../../staticData/currencies';
import ModernTable from './components/ModernTable';
import UpdateProduct from '../items/UpdateProduct';
import { userLimits } from '../dashboard/uitls/userLimits';
import Swal from 'sweetalert2';
import {
  TextField,
  Card,
  Typography,
  Box,
  Chip,
  Button,
  Grid,
  alpha,
  Paper,
  InputAdornment,
  ButtonGroup,
  Dialog,
  DialogContent,
  IconButton,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';

import { useState, useEffect, useContext, useRef } from 'react';
import { UserContext } from '../contexts/userContext';
import { CloudDownload } from '@mui/icons-material';
import { ExportToCsv } from 'export-to-csv';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import CategoryIcon from '@mui/icons-material/Category';
import QrCodeIcon from '@mui/icons-material/QrCode';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import InventoryIcon from '@mui/icons-material/Inventory';
import { imageOnError } from '../../services/method/methods';

// Product Media Viewer Component with Slider
function ProductMediaViewer({ open, onClose, product, referTo }) {
  const theme = useTheme();
  const [currentIndex, setCurrentIndex] = useState(0);

  // Build media array (images + video)
  const mediaItems = [];

  if (product.productPictures && product.productPictures.length > 0) {
    product.productPictures.slice(0, 3).forEach((pic) => {
      mediaItems.push({
        type: 'image',
        url: `${process.env.REACT_APP_SERVER_API}/pictures/${pic}/${referTo}`,
      });
    });
  }

  if (product.productVideo) {
    mediaItems.push({
      type: 'video',
      url: `${process.env.REACT_APP_SERVER_API}/videos/${product.productVideo}/${referTo}`,
    });
  }

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? mediaItems.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === mediaItems.length - 1 ? 0 : prev + 1));
  };

  const handleClose = () => {
    setCurrentIndex(0);
    onClose();
  };

  if (mediaItems.length === 0) return null;

  const currentMedia = mediaItems[currentIndex];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.paper,
          borderRadius: 3,
          maxHeight: '90vh',
        },
      }}
    >
      {/* Close Button */}
      <IconButton
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          zIndex: 1,
          backgroundColor: alpha(theme.palette.background.paper, 0.8),
          backdropFilter: 'blur(8px)',
          '&:hover': {
            backgroundColor: alpha(theme.palette.background.paper, 0.95),
          },
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent sx={{ p: 0, position: 'relative' }}>
        {/* Product Info Header */}
        <Box
          sx={{
            p: 3,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
            {product.name?.en}
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.5 }}>
            UPC: {product.upc}
          </Typography>
        </Box>

        {/* Media Display Container with Navigation */}
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '24px auto',
            maxWidth: 600, // Wider to accommodate buttons outside
          }}
        >
          {/* Left Navigation Arrow */}
          {mediaItems.length > 1 && (
            <IconButton
              onClick={handlePrev}
              sx={{
                position: 'absolute',
                left: -60,
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                backdropFilter: 'blur(8px)',
                border: `1px solid ${theme.palette.divider}`,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  borderColor: theme.palette.primary.main,
                },
              }}
            >
              <ChevronLeftIcon />
            </IconButton>
          )}

          {/* Media Display - Fixed size with 180:160 ratio */}
          <Box
            sx={{
              width: 450, // Fixed width (2.5x of 180px)
              height: 400, // Fixed height (2.5x of 160px) - maintains 180:160 ratio
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: alpha(
                theme.palette.grey[100],
                theme.palette.mode === 'dark' ? 0.1 : 1
              ),
            }}
          >
            {currentMedia.type === 'image' ? (
              <Box
                component="img"
                src={currentMedia.url}
                alt={product.name?.en}
                onError={imageOnError}
                sx={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                  p: 2,
                }}
              />
            ) : (
              <Box
                component="video"
                src={currentMedia.url}
                controls
                autoPlay
                sx={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                }}
              />
            )}
          </Box>

          {/* Right Navigation Arrow */}
          {mediaItems.length > 1 && (
            <IconButton
              onClick={handleNext}
              sx={{
                position: 'absolute',
                right: -60,
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                backdropFilter: 'blur(8px)',
                border: `1px solid ${theme.palette.divider}`,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.background.paper, 0.95),
                  borderColor: theme.palette.primary.main,
                },
              }}
            >
              <ChevronRightIcon />
            </IconButton>
          )}
        </Box>

        {/* Media Indicators */}
        {mediaItems.length > 1 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 1,
              p: 2,
              borderTop: `1px solid ${theme.palette.divider}`,
            }}
          >
            {mediaItems.map((media, index) => (
              <Box
                key={index}
                onClick={() => setCurrentIndex(index)}
                sx={{
                  width: 60,
                  height: 60,
                  borderRadius: 1,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  border: `2px solid ${
                    index === currentIndex ? theme.palette.primary.main : theme.palette.divider
                  }`,
                  opacity: index === currentIndex ? 1 : 0.6,
                  transition: 'all 0.2s',
                  position: 'relative',
                  '&:hover': {
                    opacity: 1,
                  },
                }}
              >
                {media.type === 'image' ? (
                  <Box
                    component="img"
                    src={media.url}
                    alt={`Preview ${index + 1}`}
                    onError={imageOnError}
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: alpha(theme.palette.grey[900], 0.8),
                    }}
                  >
                    <PlayCircleOutlineIcon sx={{ color: 'white', fontSize: 24 }} />
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Clean Modern Product Card Component with Lazy Loading
function ModernProductCard({ product, onEdit, onDelete, referTo, onImageClick }) {
  const theme = useTheme();
  const [shouldLoadImage, setShouldLoadImage] = useState(false);
  const _portalUser = JSON.parse(localStorage.getItem('user'));
  const _currencySymbol = currencies.find((c) => c.code === (_portalUser?.primaryCurrency || 'USD'))?.symbol || '$';
  const cardRef = useRef(null);
  const timeoutRef = useRef(null);

  const imageUrl =
    product.productPictures?.length > 0
      ? `${process.env.REACT_APP_SERVER_API}/pictures/${product.productPictures[0]}/${referTo}?quality=80`
      : null;

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Element is in viewport, start 1.5 second timer
            timeoutRef.current = setTimeout(() => {
              setShouldLoadImage(true);
            }, 1500);
          } else {
            // Element left viewport, clear timer
            if (timeoutRef.current) {
              clearTimeout(timeoutRef.current);
              timeoutRef.current = null;
            }
          }
        });
      },
      {
        threshold: 0.1, // Trigger when 10% of the element is visible
        rootMargin: '50px', // Start loading a bit before element enters viewport
      }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => {
      if (cardRef.current) {
        observer.unobserve(cardRef.current);
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <Card
      ref={cardRef}
      sx={{
        height: '100%',
        borderRadius: 3,
        overflow: 'hidden',
        background: theme.palette.background.paper,
        boxShadow: `0 1px 3px ${alpha(theme.palette.common.black, 0.06)}`,
        border: `1px solid ${theme.palette.divider}`,
        transition: 'all 0.3s ease',
        display: 'flex',
        flexDirection: 'column',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
          borderColor: alpha(theme.palette.primary.main, 0.3),
        },
      }}
    >
      {/* Product Image */}
      <Box
        onClick={onImageClick}
        sx={{
          position: 'relative',
          backgroundColor:
            theme.palette.mode === 'dark'
              ? alpha(theme.palette.background.default, 0.5)
              : alpha(theme.palette.grey[100], 1),
          paddingTop: '77.27%',
          overflow: 'hidden',
          cursor: 'pointer',
          '&:hover': {
            '& img': {
              transform: 'scale(1.05)',
            },
          },
        }}
      >
        {shouldLoadImage && imageUrl ? (
          <Box
            component="img"
            src={imageUrl}
            alt={product.name?.en}
            onError={imageOnError}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              padding: 2,
              backgroundColor: theme.palette.mode === 'dark' ? alpha('#fff', 0.02) : 'transparent',
              transition: 'transform 0.3s ease',
            }}
          />
        ) : (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: alpha(theme.palette.text.secondary, 0.3),
            }}
          >
            <QrCodeIcon sx={{ fontSize: 60 }} />
          </Box>
        )}

        {product.prodType === 'bundle' && (
          <Chip
            label="Bundle"
            size="small"
            sx={{
              position: 'absolute',
              top: 10,
              left: 10,
              backgroundColor: theme.palette.warning.main,
              color: theme.palette.warning.contrastText,
              fontWeight: 600,
              fontSize: '0.7rem',
              height: 24,
            }}
          />
        )}
      </Box>

      {/* Product Info */}
      <Box sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            color: theme.palette.text.primary,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            mb: 0.25,
            minHeight: '2.2em',
            lineHeight: 1.2,
            fontSize: '0.8rem',
          }}
        >
          {product.name?.en}
        </Typography>

        <Typography
          variant="caption"
          sx={{
            color: theme.palette.text.secondary,
            mb: 0.5,
            fontSize: '0.7rem',
            fontFamily: 'monospace',
            display: 'block',
          }}
        >
          {product.upc}
        </Typography>

        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 700,
            color: theme.palette.primary.main,
            mb: 1,
            fontSize: '0.9rem',
            lineHeight: 1.2,
          }}
        >
          {_currencySymbol}{parseFloat(product.price).toFixed(2)}
        </Typography>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: { xs: 0.5, md: 1 }, mt: 'auto' }}>
          <Button
            onClick={onEdit}
            variant="outlined"
            size="small"
            startIcon={<EditIcon sx={{ fontSize: { xs: 12, md: 14 } }} />}
            fullWidth
            sx={{
              py: 0.5,
              px: { xs: 0.5, md: 1 },
              fontSize: { xs: '0.65rem', md: '0.7rem' },
              fontWeight: 600,
              borderRadius: 1.5,
              textTransform: 'none',
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.main,
              minWidth: 'auto',
              '& .MuiButton-startIcon': {
                marginRight: { xs: '2px', md: '4px' },
                marginLeft: 0,
              },
              '&:hover': {
                borderColor: theme.palette.primary.dark,
                backgroundColor: alpha(theme.palette.primary.main, 0.04),
              },
            }}
          >
            Edit
          </Button>
          <Button
            onClick={onDelete}
            variant="outlined"
            size="small"
            startIcon={<DeleteIcon sx={{ fontSize: { xs: 12, md: 14 } }} />}
            fullWidth
            sx={{
              py: 0.5,
              px: { xs: 0.5, md: 1 },
              fontSize: { xs: '0.65rem', md: '0.7rem' },
              fontWeight: 600,
              borderRadius: 1.5,
              textTransform: 'none',
              borderColor: theme.palette.error.main,
              color: theme.palette.error.main,
              minWidth: 'auto',
              '& .MuiButton-startIcon': {
                marginRight: { xs: '2px', md: '4px' },
                marginLeft: 0,
              },
              '&:hover': {
                borderColor: theme.palette.error.dark,
                backgroundColor: alpha(theme.palette.error.main, 0.04),
              },
            }}
          >
            Delete
          </Button>
        </Box>
      </Box>
    </Card>
  );
}

function ManageProducts(props) {
  const theme = useTheme();
  const { setProductsDash, setView, view } = props;
  const { access } = userLimits('', '', 'Products', 'edit_prod');
  const { userEmail, role, referTo } = useContext(UserContext);
  const userId = props.user;
  const portalUser = JSON.parse(localStorage.getItem('user'));
  const primaryCurrencyCode = portalUser?.primaryCurrency || 'USD';
  const currencySymbol = currencies.find((c) => c.code === primaryCurrencyCode)?.symbol || '$';
  const id = role === 'Admin' || role === 'SuperAdmin' ? (userId ? userId : userEmail) : referTo;
  const getAllProdURL = `${process.env.REACT_APP_SERVER_API}/products/`;

  const [prods, setProds] = useState([]);

  const [rowD, setRowD] = useState({});
  const [updateShow, setUpdateShow] = useState(false);
  const [input, setInput] = useState('');
  const [mediaViewerOpen, setMediaViewerOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const [pagination, setPagination] = useState(
    localStorage.getItem('table_view_prod') ? localStorage.getItem('table_view_prod') : 20
  );

  useEffect(() => {
    localStorage.setItem('table_view_prod', pagination);
  }, [pagination]);

  useEffect(() => {
    console.log('prods', prods);
  }, [prods]);

  const downloadProducts = () => {
    // console.log(prods);
    // return console.log(prods);
    const result = prods.map((e) => {
      let newCate = e.category.map((e) => e?.name?.en);
      let cate = newCate.join(', ');

      let image1 = '';
      let image2 = '';
      let image3 = '';
      let video = '';
      if (e.productPictures?.length > 0) {
        image1 = `${process.env.REACT_APP_SERVER_API}/image/${e.referTo}/${e.productPictures[0]}`;
      }
      if (e.productPictures?.length > 1) {
        image2 = `${process.env.REACT_APP_SERVER_API}/image/${e.referTo}/${e.productPictures[1]}`;
      }
      if (e.productPictures?.length > 2) {
        image3 = `${process.env.REACT_APP_SERVER_API}/image/${e.referTo}/${e.productPictures[2]}/`;
      }
      if (e.productVideo) {
        video = `${process.env.REACT_APP_SERVER_API}/video/${e.referTo}/${e.productVideo}`;
      }
      return {
        name: e.name?.en,
        upc: `${e.upc}`,
        description: e.description?.en,
        brand: e.brand?.name?.en ? e.brand?.name?.en : '',
        category: cate,
        price: e.price,
        costPrice: e.costPrice ? e.costPrice : '',
        width: e.dimension?.length,
        height: e.dimension?.height,
        depth: e.dimension?.width,
        image1: image1,
        image2: image2,
        image3: image3,
        video: video,
      };
    });
    console.log('result', result);

    const options = {
      fieldSeparator: ',',
      quoteStrings: '"',
      decimalSeparator: '.',
      useTextFile: false,
      filename: 'Sales Report CSV(products report per machine per day)',
      useKeysAsHeaders: true,
    };

    const csvExporter = new ExportToCsv(options);
    console.log(csvExporter, 'csvExporter');
    csvExporter.generateCsv(result);
  };

  const columns = [
    {
      title: 'Name',
      field: 'name.en',
      render: (rowData) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
          {rowData.name?.en}
        </Typography>
      ),
    },
    {
      title: 'UPC',
      field: 'upc',
      render: (rowData) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
          {rowData.upc}
        </Typography>
      ),
    },
    {
      title: 'Price',
      field: 'price',
      render: (rowData) => (
        <Chip
          label={`${currencySymbol}${rowData.price.toFixed(2)}`}
          sx={{
            fontWeight: 700,
            backgroundColor: alpha(theme.palette.success.main, 0.12),
            color: theme.palette.success.dark,
            border: `1.5px solid ${alpha(theme.palette.success.main, 0.3)}`,
          }}
        />
      ),
    },
    {
      title: 'Brand',
      field: 'brand.name.en',
      render: (rowData) =>
        rowData.brand?.name?.en ? (
          <Chip
            icon={<LocalOfferIcon sx={{ fontSize: 14 }} />}
            label={rowData.brand.name.en}
            size="small"
            sx={{
              backgroundColor: alpha(theme.palette.info.main, 0.1),
              color: theme.palette.info.dark,
              border: `1px solid ${alpha(theme.palette.info.main, 0.25)}`,
              '& .MuiChip-icon': {
                color: theme.palette.info.dark,
              },
            }}
          />
        ) : (
          <Typography variant="body2" color="text.secondary">
            -
          </Typography>
        ),
    },
    {
      title: 'Categories',
      field: 'category',
      render: (rowData) => (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {rowData.category?.slice(0, 2).map((cat, index) => (
            <Chip
              key={index}
              icon={<CategoryIcon sx={{ fontSize: 14 }} />}
              label={cat.name?.en}
              size="small"
              sx={{
                backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                color: theme.palette.secondary.dark,
                fontSize: '0.75rem',
                border: `1px solid ${alpha(theme.palette.secondary.main, 0.25)}`,
                '& .MuiChip-icon': {
                  color: theme.palette.secondary.dark,
                },
              }}
            />
          ))}
          {rowData.category?.length > 2 && (
            <Chip
              label={`+${rowData.category.length - 2}`}
              size="small"
              sx={{
                backgroundColor: alpha(theme.palette.text.secondary, 0.1),
                color: theme.palette.text.secondary,
                fontSize: '0.75rem',
              }}
            />
          )}
        </Box>
      ),
    },
  ];

  useEffect(() => {
    request({
      method: 'get',
      url: role === 'SuperAdmin' && !userId ? getAllProdURL : getAllProdURL + id,
    })
      .then((res) => {
        console.log(res.data);
        setProds(res.data);
        setProductsDash(res.data);
      })
      .catch((err) => {
        console.log(err);
      });
    return () => {
      setProds([]);
      setProductsDash([]);
    };
  }, []);

  const filteredProduct = prods.filter((prod) => prod.category === props.catename);

  let result;

  if (!props.catename) result = [...prods];
  else result = [...filteredProduct];

  if (input.length > 0) {
    result = result.filter(
      (product) =>
        product.name.en?.toLowerCase().includes(input.toLowerCase()) ||
        product.upc?.toLowerCase().includes(input.toLowerCase())
    );
  }

  const handleEditProduct = (product) => {
    setRowD(product);
    setUpdateShow(true);
  };

  const handleDeleteProduct = (product) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
    }).then((result) => {
      if (result.isConfirmed) {
        request({
          method: 'delete',
          url: `${process.env.REACT_APP_SERVER_API}/products/delete/${product._id}`,
        })
          .then(() => {
            Swal.fire({
              text: `The product has been successfully deleted: ${product.name.en}`,
              icon: 'success',
            });
            setTimeout(() => {
              window.location.reload();
            }, 1000);
          })
          .catch((err) => {
            if (err.response?.data?.message) {
              Swal.fire('', err.response?.data?.message, 'error');
            }
          });
      }
    });
  };

  const handleImageClick = (product) => {
    setSelectedProduct(product);
    setMediaViewerOpen(true);
  };

  let products = [];
  let bundle = [];

  result.forEach((prod) => {
    const card = (
      <Grid item xs={6} sm={4} md={3} lg={2} key={prod._id}>
        <ModernProductCard
          product={prod}
          referTo={id}
          onEdit={() => handleEditProduct(prod)}
          onDelete={() => handleDeleteProduct(prod)}
          onImageClick={() => handleImageClick(prod)}
        />
      </Grid>
    );

    if (prod.prodType === 'bundle') {
      bundle.push(card);
    } else {
      products.push(card);
    }
  });

  function productStructure() {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: `linear-gradient(135deg, ${alpha(
            theme.palette.primary.main,
            0.02
          )} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
          px: { xs: 1, sm: 1.5, md: 3, lg: 5 },
          py: { xs: 1, sm: 1.5, md: 2, lg: 2.5 },
        }}
      >
        {/* Header with User Info */}
        {role === 'SuperAdmin' && userId && (
          <Paper
            sx={{
              p: 2,
              mb: 3,
              borderRadius: 2,
              backgroundColor: alpha(theme.palette.info.main, 0.1),
              border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
            }}
          >
            <Typography variant="h6" sx={{ color: theme.palette.info.dark, fontWeight: 600 }}>
              Products of: {userId}
            </Typography>
          </Paper>
        )}

        {/* Modern View Toggle Buttons */}
        <Paper
          sx={{
            p: 1,
            mb: 3,
            borderRadius: 3,
            display: 'inline-flex',
            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.08)}`,
            border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
          }}
        >
          <ButtonGroup
            sx={{
              gap: 1,
              '& .MuiButtonGroup-grouped': {
                border: 0,
              },
            }}
          >
            <Button
              onClick={() => setView('list')}
              startIcon={<ViewListIcon />}
              sx={{
                px: 3,
                py: 1.5,
                borderRadius: 2,
                fontWeight: 700,
                fontSize: '0.875rem',
                textTransform: 'none',
                color: view === 'list' ? 'white' : theme.palette.text.secondary,
                background:
                  view === 'list'
                    ? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
                    : 'transparent',
                boxShadow:
                  view === 'list' ? `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}` : 'none',
                '&:hover': {
                  background:
                    view === 'list'
                      ? `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`
                      : alpha(theme.palette.primary.main, 0.08),
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              List View
            </Button>
            <Button
              onClick={() => setView('module')}
              startIcon={<ViewModuleIcon />}
              sx={{
                px: 3,
                py: 1.5,
                borderRadius: 2,
                fontWeight: 700,
                fontSize: '0.875rem',
                textTransform: 'none',
                color: view === 'module' ? 'white' : theme.palette.text.secondary,
                background:
                  view === 'module'
                    ? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
                    : 'transparent',
                boxShadow:
                  view === 'module'
                    ? `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
                    : 'none',
                '&:hover': {
                  background:
                    view === 'module'
                      ? `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`
                      : alpha(theme.palette.primary.main, 0.08),
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              Products
            </Button>
            <Button
              onClick={() => setView('bundle')}
              startIcon={<InventoryIcon />}
              sx={{
                px: 3,
                py: 1.5,
                borderRadius: 2,
                fontWeight: 700,
                fontSize: '0.875rem',
                textTransform: 'none',
                color: view === 'bundle' ? 'white' : theme.palette.text.secondary,
                background:
                  view === 'bundle'
                    ? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
                    : 'transparent',
                boxShadow:
                  view === 'bundle'
                    ? `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
                    : 'none',
                '&:hover': {
                  background:
                    view === 'bundle'
                      ? `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`
                      : alpha(theme.palette.primary.main, 0.08),
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              Bundles
            </Button>
          </ButtonGroup>
        </Paper>

        {/* LIST VIEW */}
        {view === 'list' && (
          <Box>
            <ModernTable
              onRowsPerPageChange={setPagination}
              columns={columns}
              data={result}
              actions={[
                {
                  icon: CloudDownload,
                  tooltip: 'Download all products',
                  isFreeAction: true,
                  onClick: () => downloadProducts(),
                },
                {
                  icon: 'edit',
                  tooltip: 'Edit Product',
                  onClick: (_event, rowData) => handleEditProduct(rowData),
                },
                {
                  icon: 'delete',
                  tooltip: 'Delete Product',
                  onClick: (_event, rowData) => handleDeleteProduct(rowData),
                },
              ]}
              options={{
                pageSize: parseInt(pagination),
                pageSizeOptions: [20, 50, 100, 200],
                actionsColumnIndex: -1,
              }}
            />
          </Box>
        )}

        {/* PRODUCTS VIEW */}
        {view === 'module' && (
          <Box>
            {/* Search Bar */}
            <Paper
              sx={{
                p: 2,
                mb: 3,
                borderRadius: 3,
                backgroundColor: theme.palette.background.paper,
                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.08)}`,
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <TextField
                fullWidth
                placeholder="Search products by name or UPC..."
                variant="outlined"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: theme.palette.text.secondary }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor:
                      theme.palette.mode === 'dark'
                        ? alpha(theme.palette.background.default, 0.5)
                        : theme.palette.background.paper,
                  },
                }}
              />
            </Paper>

            {/* Products Grid */}
            {products.length > 0 ? (
              <Grid container spacing={{ xs: 1.5, sm: 2, md: 3, lg: 3, xl: 5 }}>
                {products}
              </Grid>
            ) : (
              <Paper
                sx={{
                  p: 8,
                  textAlign: 'center',
                  borderRadius: 3,
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <QrCodeIcon
                  sx={{ fontSize: 80, color: alpha(theme.palette.text.secondary, 0.3), mb: 2 }}
                />
                <Typography variant="h6" color="text.secondary">
                  No products found
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Try adjusting your search criteria
                </Typography>
              </Paper>
            )}
          </Box>
        )}

        {/* BUNDLE VIEW */}
        {view === 'bundle' && (
          <Box>
            {/* Search Bar */}
            <Paper
              sx={{
                p: 2,
                mb: 3,
                borderRadius: 3,
                backgroundColor: theme.palette.background.paper,
                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.08)}`,
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <TextField
                fullWidth
                placeholder="Search bundles by name or UPC..."
                variant="outlined"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: theme.palette.text.secondary }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor:
                      theme.palette.mode === 'dark'
                        ? alpha(theme.palette.background.default, 0.5)
                        : theme.palette.background.paper,
                  },
                }}
              />
            </Paper>

            {/* Bundles Grid */}
            {bundle.length > 0 ? (
              <Grid container spacing={{ xs: 1.5, sm: 2, md: 3, lg: 3, xl: 5 }}>
                {bundle}
              </Grid>
            ) : (
              <Paper
                sx={{
                  p: 8,
                  textAlign: 'center',
                  borderRadius: 3,
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <QrCodeIcon
                  sx={{ fontSize: 80, color: alpha(theme.palette.text.secondary, 0.3), mb: 2 }}
                />
                <Typography variant="h6" color="text.secondary">
                  No bundles found
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Try adjusting your search criteria
                </Typography>
              </Paper>
            )}
          </Box>
        )}

        {/* Update Product Modal */}
        <UpdateProduct
          access={access}
          id={rowD._id}
          productsDash={prods}
          name={rowD.name}
          upc={rowD.upc}
          description={rowD.description}
          category={rowD.category}
          price={rowD.price}
          video={rowD.video}
          brand={rowD.brand}
          quantity={rowD.quantity}
          referTo={rowD.referTo}
          show={updateShow}
          onHide={() => setUpdateShow(false)}
        />

        {/* Product Media Viewer */}
        {selectedProduct && (
          <ProductMediaViewer
            open={mediaViewerOpen}
            onClose={() => setMediaViewerOpen(false)}
            product={selectedProduct}
            referTo={id}
          />
        )}
      </Box>
    );
  }
  return <>{productStructure()}</>;
}

export default ManageProducts;
